import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress, Button } from "@mui/material";
import { fetchQuery } from "../../utils/commonComponent/fetched";
import { toast } from "react-toastify";
import { certQuery } from "./certQuery";
import { CancelButton } from "../../utils/commonComponent/Custom/Button";
import { useNavigate } from "react-router-dom";

const MultipleCertificateDownload = ({ _userType, showJobAssign, onJobAssignClick }) => {
  const [loading, setLoading] = useState(false);
  const [labRefOptions, setLabRefOptions] = useState([]);
  const [selectedLabRef, setSelectedLabRef] = useState(null);

  const [testLoading, setTestLoading] = useState(false);
  const [testOptions, setTestOptions] = useState([]);
  const [fromTest, setFromTest] = useState(null);
  const [toTest, setToTest] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [status, setStatus] = useState("");

  const navigate = useNavigate();
  // Load Lab Reference options
  useEffect(() => {
    const fetchLabRefs = async () => {
      setLoading(true);
      try {
        const query = `SELECT DISTINCT inward.id AS inward_id, inward.labReferenceNo AS inward_labReferenceNo FROM inward WHERE inward.labReferenceNo IS NOT NULL AND inward.labReferenceNo != '-' AND inward.status != -1 ORDER BY inward.id DESC;`;
        const result = await fetchQuery(query);
        const refs = result
          .filter((row) => row.inward_labReferenceNo?.trim())
          .map((row) => ({
            id: row.inward_id,
            label: row.inward_labReferenceNo.trim(),
          }));
        setLabRefOptions(refs);
      } catch (error) {
        console.error("Error fetching lab references:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLabRefs();
  }, []);

  // Handle Lab Reference selection
  const handleReferenceSelect = async (newValue) => {
    setSelectedLabRef(newValue);
    setFromTest(null);
    setToTest(null);
    setSelectedIds([]);
    setTestOptions([]);

    if (!newValue?.id) return;

    showJobAssign(newValue);
    setTestLoading(true);
    try {
      const query = `${certQuery} WHERE observationSheet.status != -1 AND inward.id = ${newValue.id} ORDER BY observationSheet_testNumber ASC;`;
      const result = await fetchQuery(query);

      const tests = result
        ?.filter((row) => row.observationSheet_testNumber)
        ?.map((row) => ({
          id: row.id,
          label: row.observationSheet_testNumber,
        }));

      const sortedTests = [...tests].sort((a, b) => {
        const numA = parseInt(a.label.split("/").pop());
        const numB = parseInt(b.label.split("/").pop());
        return numA - numB;
      });

      setTestOptions(sortedTests);
    } catch (error) {
      console.error("Error fetching test numbers:", error);
    } finally {
      setTestLoading(false);
    }
  };

  // Cancel handler
  const handleCancel = () => {
    setSelectedLabRef(null);
    setFromTest(null);
    setToTest(null);
    setSelectedIds([]);
    showJobAssign(null);
    onJobAssignClick();
  };

  // Handle range selection
  const handleRangeSelect = (from, to) => {
    setFromTest(from);
    setToTest(to);

    if (!from || !to) {
      setSelectedIds([]);
      return;
    }

    const fromIndex = testOptions.findIndex((t) => t.id === from.id);
    const toIndex = testOptions.findIndex((t) => t.id === to.id);

    if (fromIndex === -1 || toIndex === -1) return;

    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    const ids = testOptions.slice(start, end + 1).map((t) => t.id);

    setSelectedIds(ids);
  };

  // Navigate to multi-download page
  const handleDownload = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select test range before downloading!");
      return;
    }

    let finalIds = [...selectedIds];

    try {
      let query = `${certQuery} WHERE certificate.id IN (${selectedIds.join(",")}) AND certificate.status != -1`;

      if (status === "Approved") {
        query += ` AND certificate.authorizedBy IS NOT NULL AND certificate.authorizedBy != ''`;
      } else if (status === "Draft") {
        query += ` AND (certificate.authorizedBy IS NULL OR certificate.authorizedBy = '' )`;
      }

      const result = await fetchQuery(query);
      finalIds = result.map((row) => row.id);

      if (finalIds.length === 0) {
        toast.info(`No ${status.toLowerCase()} certificates found for the selected range.`);
        return;
      }

      const idString = finalIds.join(",");
      console.log("Navigating with IDs:", idString);
      navigate(`/viewCertificate/${idString}`);
    } catch (error) {
      console.error("Error filtering certificates:", error);
      toast.error("Something went wrong while filtering certificates.");
    }
  };

  // Show only for Admin/HOD/Super Admin
  if (![1, 2, 3].includes(_userType)) return null;

  return (
    <div className="p-2 flex items-center gap-3 flex-wrap">
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          {/* Status Selector */}
          <Autocomplete
            size="small"
            sx={{ width: 160 }}
            options={["Draft", "Approved"]}
            value={status}
            onChange={(e, newValue) => {
              setStatus(newValue);
              setSelectedLabRef(null);
              setFromTest(null);
              setToTest(null);
              setSelectedIds([]);
            }}
            renderInput={(params) => <TextField {...params} label="Select Type" />}
          />

          {/* Lab Reference */}
          <Autocomplete
            size="small"
            sx={{ width: 250 }}
            options={labRefOptions}
            value={selectedLabRef}
            onChange={(e, newValue) => handleReferenceSelect(newValue)}
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => <TextField {...params} label="Lab Reference No" />}
            disabled={!status}
          />

          {/* From Test */}
          <Autocomplete
            size="small"
            sx={{ width: 180 }}
            options={testOptions}
            value={fromTest}
            loading={testLoading}
            onChange={(e, newValue) => handleRangeSelect(newValue, toTest)}
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => <TextField {...params} label="From Test" />}
            disabled={!selectedLabRef}
          />

          {/* To Test */}
          <Autocomplete
            size="small"
            sx={{ width: 180 }}
            options={[...testOptions].reverse()}
            value={toTest}
            loading={testLoading}
            onChange={(e, newValue) => handleRangeSelect(fromTest, newValue)}
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => <TextField {...params} label="To Test" />}
            disabled={!selectedLabRef}
          />

          {/* Download Button */}
          <Button
            variant="contained"
            color="primary"
            disabled={selectedIds.length === 0}
            onClick={handleDownload}
          >
            Download
          </Button>

          {/* Cancel Button */}
          <CancelButton outline label="Cancel" onClick={handleCancel} />
        </>
      )}
    </div>
  );
};

export default MultipleCertificateDownload;
